import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
 Check,
 ArrowRight,
 Sparkles,
 Clock,
 Store,
 Wallet,
 Users,
 Gift,
 Megaphone,
 ChartLine,
 BellRing,
 Layers,
 Crown,
 ShoppingBag,
 Zap,
 TrendingUp,
 ShieldCheck,
 Award,
 Star,
 Repeat,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import OnboardingShell from '../components/OnboardingShell';
import FastInfoModal from '../components/FastInfoModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NoFee Ordering — comprehensive feature list grouped by category.
 * Each group renders as a collapsible-style block so the card stays
 * scannable but the full value is communicated.
 */
const ORDERING_FEATURE_GROUPS = [
 {
 Icon: ShoppingBag,
 title: 'Direct Ordering',
 items: [
 '0% commission on every order',
 'Direct restaurant ordering — you own the customer',
 'Stripe Connect + — direct payouts to your bank',
 'QR-code ordering · pickup · dine-in · table service',
 ],
 },
 {
 Icon: Layers,
 title: 'Menu Management',
 items: [
 'Unlimited menu items & photos',
 'Categories, modifiers, add-ons',
 'Upsells & combo meals',
 ],
 },
 {
 Icon: Users,
 title: 'Customer Experience',
 items: [
 'Customer accounts & favorites',
 'Order history & recently visited',
 'Saved customer info · faster checkout',
 'Personal Rewards dashboard',
 ],
 },
 {
 Icon: Gift,
 title: 'Rewards System',
 items: [
 'Rewards points + gift credits',
 'Gift redemption at participating restaurants',
 'Friend & restaurant referral rewards',
 ],
 },
 {
 Icon: BellRing,
 title: 'Communication',
 items: [
 'SMS + WhatsApp order notifications',
 'Real-time order status alerts',
 'Customer alerts for promos & updates',
 ],
 },
 {
 Icon: ChartLine,
 title: 'Business Tools',
 items: [
 'Sales & customer dashboards',
 'Order management',
 'Analytics & reporting',
 ],
 },
 {
 Icon: Wallet,
 title: 'Payments & Payouts',
 items: [
 'Direct Stripe payouts',
 'Reimbursement tracking',
 'Restaurant payment history',
 ],
 },
 {
 Icon: Megaphone,
 title: 'Marketing',
 items: [
 'Coupons · promotions · discounts',
 'Loyalty campaigns',
 ],
 },
];

/**
 * NoFee Pro — positioned as a complete Restaurant Management & Growth Platform
 * (NOT just a POS). Coming Soon.
 */
const PRO_FEATURE_GROUPS = [
 {
 title: 'Everything in NoFee Ordering',
 items: ['All features from the Ordering plan, included.'],
 foundation: true,
 },
 {
 title: 'Restaurant POS',
 items: [
 'Full POS system · tablet · counter · mobile',
 'Kitchen display integration',
 ],
 },
 {
 title: 'Table Service',
 items: [
 'Table management & waiter assignments',
 'Open tables · transfers · server tracking',
 ],
 },
 {
 title: 'Split Checks',
 items: [
 'Split by customer or by items',
 'Equal split · multiple payment methods',
 ],
 },
 {
 title: 'Advanced Dining',
 items: [
 'Dine-in management',
 'Table-side ordering & payments',
 'Customer self-ordering at the table',
 ],
 },
 {
 title: 'Staff Management',
 items: [
 'Employee accounts & role permissions',
 'Shift management · activity logs',
 ],
 },
 {
 title: 'Customer CRM',
 items: [
 'Customer profiles · visit frequency · spending history',
 'VIP customers & segmentation',
 ],
 },
 {
 title: 'Advanced Marketing',
 items: [
 'Email + SMS marketing campaigns',
 'Automated promotions · birthday rewards',
 'Customer reactivation campaigns',
 ],
 },
 {
 title: 'Advanced Analytics',
 items: [
 'Best-selling items · revenue trends',
 'Customer retention · avg ticket value',
 'Performance reports',
 ],
 },
 {
 title: 'Growth Tools',
 items: [
 'Loyalty · referral · reward · gift-credit automation',
 ],
 },
 {
 title: 'Multi-location',
 items: [
 'Multiple restaurant locations',
 'Centralized management · unified reporting',
 ],
 },
];

const FeatureGroup = ({ Icon, title, items, tone = 'gold', foundation = false }) => (
 <div
 className={`p-4 rounded-2xl border ${
 foundation
? 'border-emerald-500/30 bg-emerald-500/[0.05]'
: 'border-white/8 bg-white/[0.02]'
 }`}
 >
 <div className="flex items-center gap-2 mb-2">
 {Icon? (
 <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
 tone === 'gold'? 'bg-yellow-500/15 border border-yellow-500/35': 'bg-white/[0.04] border border-white/10'
 }`}>
 <Icon className={`w-3.5 h-3.5 ${tone === 'gold'? 'text-yellow-300': 'text-white/70'}`} strokeWidth={1.85} />
 </span>
 ): foundation? (
 <span className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/35 flex items-center justify-center shrink-0">
 <Check className="w-3.5 h-3.5 text-emerald-300" />
 </span>
 ): null}
 <h4 className={`text-[12.5px] font-bold uppercase tracking-[0.12em] ${
 foundation? 'text-emerald-300': tone === 'gold'? 'text-yellow-300': 'text-white/70'
 }`}>
 {title}
 </h4>
 </div>
 <ul className="space-y-1 pl-1">
 {items.map((it) => (
 <li key={it} className="flex items-start gap-2 text-[12.5px] text-white/75 leading-relaxed">
 <span className={`mt-1.5 w-1 h-1 rounded-full shrink-0 ${
 foundation? 'bg-emerald-300/85': tone === 'gold'? 'bg-yellow-400/85': 'bg-white/40'
 }`} />
 <span>{it}</span>
 </li>
 ))}
 </ul>
 </div>
);

const OnboardingPlanPage = () => {
 const navigate = useNavigate();
 const { token } = useAuth();
 const [submitting, setSubmitting] = useState(false);
 const [fastInfoOpen, setFastInfoOpen] = useState(false);

 const onContinue = async () => {
 // Public visitor (no token) → route into the new Step 1 signup wizard
 // (iter120: was '/start?plan=ordering' which is now a redirect to the
 // same place; pointing directly is cleaner). After signup the user
 // lands back here as Step 2 with a valid token.
 if (!token) {
 navigate('/onboarding/account?plan=ordering');
 return;
 }

 // iter114 — Plan → Stripe direct. The standalone "Subscribe" step has
 // been removed from the visible wizard, so picking the Ordering plan now
 // takes the owner straight to Stripe Connect onboarding. The subscription
 // checkout itself still lives at /complete-subscription and runs after
 // Stripe Connect is complete; the customer just never sees it as its own
 // numbered step anymore.
 setSubmitting(true);
 try {
 // Best-effort advance — failing to persist the cursor must NOT block
 // the navigation, otherwise transient backend hiccups would trap the
 // owner on the plan page even though their next action is purely UI.
 try {
 await axios.post(
 `${API}/api/onboarding/advance`,
 { step: 'stripe_connect' },
 { headers: { Authorization: `Bearer ${token}` } },
 );
 } catch (advErr) {
 if (advErr?.response?.status === 401) {
 navigate('/login?role=restaurant&next=/onboarding/plan');
 return;
 }
 // Any other error is logged but swallowed — the cursor will be
 // re-derived from Stripe state next time anyway.
 }
 navigate('/onboarding/stripe-connect');
 } catch (err) {
 toast.error(err?.response?.data?.detail || 'Could not continue. Please try again.');
 } finally {
 setSubmitting(false);
 }
 };

 return (
 <OnboardingShell
 currentStep="choose_plan"
 backFallback="/onboarding/stripe-connect"
 contentMaxWidth="max-w-6xl"
 testid="onboarding-plan-page"
 >
 <div className="max-w-2xl">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
 <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
 <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
 Choose your plan
 </span>
 </div>
 <h1
 data-testid="plan-headline"
 className="mt-5 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white"
 >
 Pick your plan.{' '}
 <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 0% commission
 </span>
.
 </h1>
 <p className="mt-4 text-[15px] sm:text-base text-white/65 leading-relaxed max-w-xl">
 NoFee Ordering is the operational direct-ordering platform. NoFee Pro is the complete
 all-in-one restaurant management & growth solution — coming soon.
 </p>
 </div>

 <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
 {/* ACTIVE — NoFee Ordering */}
 <div
 data-testid="plan-card-ordering"
 className="relative flex flex-col h-full p-7 sm:p-8 rounded-3xl border border-yellow-500/40 bg-gradient-to-br from-yellow-500/[0.08] via-amber-500/[0.04] to-transparent shadow-[0_40px_100px_-30px_rgba(250,204,21,0.45)]"
 >
 <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-gradient-to-b from-yellow-300 to-yellow-500 text-black text-[10.5px] font-extrabold uppercase tracking-[0.18em] shadow-[0_8px_20px_-6px_rgba(250,204,21,0.6)]">
 Founding Partner · Available now
 </div>

 <div>
 <div className="flex items-center gap-2 mb-2">
 <Store className="w-5 h-5 text-yellow-300" />
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-[24px] sm:text-[26px] font-semibold tracking-tight text-white">
 NoFee Ordering
 </h3>
 </div>
 <p className="text-[13px] text-white/55">
 Direct ordering platform with rewards and 0% commission.
 </p>
 </div>

 <div className="mt-6" data-testid="plan-ordering-price">
 <div className="flex items-baseline gap-2.5">
 <span className="font-['Outfit',_Inter,_sans-serif] text-[44px] sm:text-[52px] font-semibold tracking-[-0.02em] bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent leading-none">
 $299
 </span>
 <span className="text-[14px] text-white/55 font-medium">/month</span>
 <span className="text-[15px] text-white/35 line-through font-medium" data-testid="plan-ordering-regular-price">
 $349
 </span>
 </div>
 <p className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40">
 <Crown className="w-3 h-3 text-yellow-300" />
 <span className="text-[10px] uppercase tracking-[0.18em] text-yellow-300 font-bold">
 Save $50/mo · First 6 months
 </span>
 </p>
 <p className="text-[17px] sm:text-[18px] text-emerald-300 font-extrabold mt-3 tracking-tight leading-snug">
 + 45-day free trial · Cancel anytime · No contracts
 </p>
 <p className="text-[11.5px] text-white/45 mt-1">
 Then $349/month after month 6 · Cancel any time before that
 </p>
 </div>

 <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
 {ORDERING_FEATURE_GROUPS.map((g) => (
 <FeatureGroup key={g.title} {...g} tone="gold" />
 ))}
 </div>

 <button
 type="button"
 onClick={onContinue}
 disabled={submitting}
 data-testid="plan-select-ordering-btn"
 className="group mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 <Sparkles className="w-4 h-4" />
 {submitting? 'Loading…': 'Claim Founding Partner · $299/mo for 6 months'}
 <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
 </button>
 </div>

 {/* COMING SOON — NoFee Pro */}
 <div
 data-testid="plan-card-nofee-pro"
 className="relative flex flex-col h-full p-7 sm:p-8 rounded-3xl border border-yellow-500/15 bg-gradient-to-br from-zinc-900/60 via-zinc-950 to-black shadow-[0_40px_100px_-30px_rgba(0,0,0,0.85)]"
 >
 {/* Centered, contained watermark — never clips the card edges */}
 <div
 aria-hidden="true"
 className="pointer-events-none absolute inset-0 rounded-3xl flex items-center justify-center overflow-hidden"
 >
 <div className="font-['Outfit',_Inter,_sans-serif] text-[260px] sm:text-[320px] font-black tracking-[-0.04em] leading-none select-none bg-gradient-to-b from-yellow-300/[0.045] via-yellow-500/[0.025] to-transparent bg-clip-text text-transparent">
 PRO
 </div>
 </div>

 <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black border border-yellow-500/40 text-yellow-300 text-[10.5px] font-extrabold uppercase tracking-[0.18em] shadow-[0_8px_20px_-6px_rgba(250,204,21,0.25)]">
 <Sparkles className="w-3 h-3" /> Early Access Waitlist
 </div>

 <div className="relative">
 <div className="flex items-center gap-2 mb-2">
 <Crown className="w-5 h-5 text-yellow-300/85" />
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-[24px] sm:text-[26px] font-semibold tracking-tight text-white">
 NoFee Pro
 </h3>
 </div>
 <p className="text-[13px] text-white/55">
 Next-generation restaurant platform — complete operations, CRM, and growth.
 </p>
 </div>

 {/* Price block matches Ordering's vertical rhythm exactly */}
 <div className="relative mt-6 flex items-baseline gap-2">
 <span className="font-['Outfit',_Inter,_sans-serif] text-[44px] sm:text-[52px] font-semibold tracking-[-0.02em] text-white/65 leading-none">
 Soon
 </span>
 <span className="text-[14px] text-white/45 font-medium">· launching 2026</span>
 </div>
 <p className="relative text-[12.5px] text-yellow-300/80 font-bold mt-1 inline-flex items-center gap-1.5">
 <Clock className="w-3.5 h-3.5" />
 In active development · Reserve your spot
 </p>

 <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
 {PRO_FEATURE_GROUPS.map((g) => (
 <FeatureGroup key={g.title} {...g} tone="muted" />
 ))}
 </div>

 <button
 type="button"
 disabled
 data-testid="plan-pro-waitlist-disabled"
 className="relative mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl border border-yellow-500/40 bg-gradient-to-b from-yellow-500/[0.08] to-transparent text-yellow-200 font-extrabold text-[14px] tracking-tight cursor-not-allowed shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
 >
 <Sparkles className="w-4 h-4" />
 Join Early Access Waitlist
 </button>
 </div>
 </div>

 {/* ──────────────────────────────────────────────────────────────
 FAST BY NOFEE — sales pitch shown AFTER the plan cards and
 BEFORE the owner completes payment. Goal (2026-06-09): drive
 adoption of the Fast program by explaining trust + visibility
 benefits in scannable form. Same NoFee black + gold language.
 ────────────────────────────────────────────────────────────── */}
 <section
 data-testid="plan-fast-by-nofee-section"
 className="relative mt-12 rounded-3xl border border-yellow-500/35 bg-gradient-to-br from-black via-zinc-950 to-black p-7 sm:p-10 shadow-[0_40px_100px_-30px_rgba(250,204,21,0.35)] overflow-hidden"
 >
 {/* Decorative diagonal lightning glow — pure CSS, doesn't intercept clicks */}
 <div
 aria-hidden="true"
 className="pointer-events-none absolute -top-24 -right-20 w-[420px] h-[420px] rounded-full bg-yellow-500/[0.07] blur-3xl"
 />
 <div
 aria-hidden="true"
 className="pointer-events-none absolute -bottom-32 -left-16 w-[360px] h-[360px] rounded-full bg-amber-500/[0.05] blur-3xl"
 />

 <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-start">
 <div className="min-w-0">
 {/* Header pill */}
 <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 text-[10.5px] font-extrabold uppercase tracking-[0.18em]">
 <Zap className="w-3 h-3 fill-yellow-300" /> Bonus program · Free with Ordering
 </div>

 <h3
 data-testid="plan-fast-title"
 className="mt-4 font-['Outfit',_Inter,_sans-serif] text-[28px] sm:text-[34px] font-semibold tracking-tight text-white flex items-center gap-2 flex-wrap"
 >
 <Zap className="w-7 h-7 text-yellow-300 fill-yellow-300" />
 <span>FAST BY NOFEE</span>
 </h3>
 <p className="mt-2 text-[15px] sm:text-[16px] text-yellow-200/85 font-semibold">
 Get more orders through verified delivery performance.
 </p>
 <p className="mt-3 text-[13.5px] text-white/65 max-w-2xl leading-relaxed">
 Customers recognize reliable restaurants and are more likely to order from
 businesses that consistently deliver on time.
 </p>

 {/* Benefits grid */}
 <ul
 data-testid="plan-fast-benefits"
 className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5"
 >
 {[
 { Icon: ShieldCheck, text: 'Increase customer confidence' },
 { Icon: TrendingUp,  text: 'Improve restaurant credibility' },
 { Icon: Check,       text: 'Highlight reliable delivery performance' },
 { Icon: Repeat,      text: 'Encourage repeat customers' },
 { Icon: Star,        text: 'Strengthen customer trust' },
 { Icon: Award,       text: 'Fast Score available inside your dashboard' },
 ].map((b, i) => (
 <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-white/85 leading-relaxed">
 <span className="mt-0.5 w-5 h-5 rounded-md bg-yellow-500/15 border border-yellow-500/35 flex items-center justify-center shrink-0">
 <b.Icon className="w-3 h-3 text-yellow-300" strokeWidth={2.4} />
 </span>
 <span>{b.text}</span>
 </li>
 ))}
 </ul>

 {/* Highlight message in gold */}
 <div className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-yellow-500/40 bg-yellow-500/[0.08]">
 <Zap className="w-4 h-4 text-yellow-300 fill-yellow-300 shrink-0" />
 <p
 data-testid="plan-fast-highlight"
 className="text-[13px] sm:text-[13.5px] font-bold text-yellow-300 leading-snug"
 >
 Fast by NoFee does not mean the fastest delivery. It means: Delivered On Time. Verified by Real Customers.
 </p>
 </div>

 <button
 type="button"
 onClick={() => setFastInfoOpen(true)}
 data-testid="plan-fast-learn-more-btn"
 className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-yellow-500/45 bg-black/40 hover:bg-yellow-500/[0.08] text-yellow-200 hover:text-yellow-100 font-bold text-[13px] tracking-tight transition-all"
 >
 <Sparkles className="w-4 h-4" />
 Learn More About Fast
 <ArrowRight className="w-4 h-4" />
 </button>
 </div>

 {/* Fast Score note — desktop only. Fast Score is now an internal
 dashboard metric per iter134 spec (no public tier ladder). */}
 <div
 aria-hidden="true"
 className="hidden lg:flex flex-col gap-3 min-w-[240px] p-5 rounded-2xl border border-white/8 bg-white/[0.02]"
 >
 <p className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-yellow-300 mb-1">
 Verified On-Time Delivery
 </p>
 <p className="text-[13px] text-white/70 leading-relaxed">
 Restaurants that consistently deliver within their promised delivery time earn Fast by NoFee recognition.
 </p>
 <p className="text-[12.5px] text-white/55 leading-relaxed">
 Fast Score remains available inside the restaurant dashboard for operational monitoring.
 </p>
 </div>
 </div>
 </section>

 {fastInfoOpen && (
 <FastInfoModal
 onClose={() => setFastInfoOpen(false)}
 />
 )}
 </OnboardingShell>
 );
};

export default OnboardingPlanPage;
